import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { formatNumber } from '@progress/kendo-intl';
import { Chart, ChartCategoryAxis, ChartCategoryAxisItem, ChartLegend, ChartSeries, ChartSeriesItem, ChartTooltip, ChartValueAxis, ChartValueAxisItem } from '@progress/kendo-react-charts';
import { ArcGauge } from '@progress/kendo-react-gauges';
import { Button, ButtonGroup, SegmentedControl } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { caretAltUpIcon, caretAltDownIcon, chevronLeftIcon, chevronRightIcon } from '@progress/kendo-svg-icons';

// Mirrors GetSalespersonDashboard.Query.GranularityValue
export enum Granularity {
  Period = 0,
  Quarter = 1,
  Year = 2,
}

// How far back the stepper may walk before it runs out of useful history.
const minOffsetByGranularity: Record<Granularity, number> = {
  [Granularity.Period]: -13,
  [Granularity.Quarter]: -8,
  [Granularity.Year]: -3,
};

export type Card = {
  Title: string;
  FormattedValue: string;
  SubFormattedValue1: string;
  SubFormattedValue2: string;
  SubFormattedValue3: string;
  SubFormattedValue4: string;
  PercentageChange: number | null;
  Change: string | null;
}

export type DailyAssignmentSeries = {
  Label: string;
  RevenueByDay: number[];
  OrderCountByDay: number[];
}

export type Goal = {
  QuarterlyCommissionBudget: number | null;
  QuarterlyRevenueGoal: number | null;
  Earned: number | null;
}

type Props = {
  cards: Card[];
  goal: Goal | null;
  dailyRevenue: number[];
  dailyOrderCount: number[];
  dailyAssignmentSeries: DailyAssignmentSeries[];
  granularity: Granularity;
  offset: number;
  periodLabel: string;
  chartCategories: string[];
  chartCategoryTitle: string;
  onGranularityChange: (granularity: Granularity) => void;
  onOffsetChange: (offset: number) => void;
}

const seriesMatches = (left: DailyAssignmentSeries, right: DailyAssignmentSeries) =>
  left.RevenueByDay.length === right.RevenueByDay.length &&
  left.OrderCountByDay.length === right.OrderCountByDay.length &&
  left.RevenueByDay.every((value, index) => value === right.RevenueByDay[index]) &&
  left.OrderCountByDay.every((value, index) => value === right.OrderCountByDay[index]);

const DashboardTab = ({ cards, goal, dailyRevenue, dailyOrderCount, dailyAssignmentSeries, granularity, offset, periodLabel, chartCategories, chartCategoryTitle, onGranularityChange, onOffsetChange }: Props) => {
  const [selectedDailySeriesLabel, setSelectedDailySeriesLabel] = useState('Total');

  const chartSeriesOptions = useMemo(() => {
    const totalSeries = { Label: 'Total', RevenueByDay: dailyRevenue, OrderCountByDay: dailyOrderCount };
    const hasDuplicateTotal = dailyAssignmentSeries.some((series) => seriesMatches(series, totalSeries));
    return hasDuplicateTotal ? dailyAssignmentSeries : [totalSeries, ...dailyAssignmentSeries];
  }, [dailyAssignmentSeries, dailyOrderCount, dailyRevenue]);

  const selectedDailySeries = useMemo(() => (
    chartSeriesOptions.find((series) => series.Label === selectedDailySeriesLabel) ?? chartSeriesOptions[0]
  ), [chartSeriesOptions, selectedDailySeriesLabel]);

  useEffect(() => {
    if (!chartSeriesOptions.some((series) => series.Label === selectedDailySeriesLabel)) {
      setSelectedDailySeriesLabel(chartSeriesOptions[0]?.Label ?? 'Total');
    }
  }, [chartSeriesOptions, selectedDailySeriesLabel]);

  const chartAssignmentFilters = <ButtonGroup>
    {chartSeriesOptions.map((option) => (
      <Button key={option.Label} onClick={() => setSelectedDailySeriesLabel(option.Label)} selected={selectedDailySeries?.Label === option.Label} togglable>{option.Label}</Button>
    ))}
  </ButtonGroup>;

  const renderCard = (card: Card, index: number) => (
    <div key={index} className="col-sm-6 col-md-4 col-lg-3 col-xl-2 mt-3">
      <div className="card h-100">
        <div className="card-body">
          <h5 className="text-muted fw-normal mt-0">{card.Title}</h5>
          <h3 className="mt-3 mb-3">{card.FormattedValue}</h3>
          {card.SubFormattedValue1 && <p className="mb-1">{card.SubFormattedValue1}</p>}
          {card.SubFormattedValue2 && <p className="mb-1">{card.SubFormattedValue2}</p>}
          {card.SubFormattedValue3 && <p className="mb-1">{card.SubFormattedValue3}</p>}
          {card.SubFormattedValue4 && <p className="mb-1">{card.SubFormattedValue4}</p>}
          {(card.PercentageChange !== null || card.Change) && <p className="mb-0 text-muted">
            {card.PercentageChange !== null && <span className={`mr-2 ${(card.PercentageChange > 0 ? "text-success" : card.PercentageChange < 0 ? "text-danger" : "")}`}>
              <SvgIcon icon={card.PercentageChange > 0 ? caretAltUpIcon : card.PercentageChange < 0 ? caretAltDownIcon : undefined} /> {formatNumber(card.PercentageChange, 'p2')}
            </span>}
            {card.Change && <span className="text-nowrap">{card.Change}</span>}
          </p>}
        </div>
      </div>
    </div>
  );

  return <React.Fragment>
    <div className="d-flex align-items-center flex-wrap mt-3">
      <SegmentedControl
        items={[
          { text: 'Month', value: Granularity.Period.toString() },
          { text: 'Quarter', value: Granularity.Quarter.toString() },
          { text: 'Year', value: Granularity.Year.toString() }
        ]}
        value={granularity.toString()}
        onChange={(value) => onGranularityChange(parseInt(value) as Granularity)}
      />
      <div className="d-flex align-items-center ml-3">
        <Button
          title="Previous"
          svgIcon={chevronLeftIcon}
          disabled={offset <= minOffsetByGranularity[granularity]}
          onClick={() => onOffsetChange(offset - 1)}
        />
        <span className="mx-2 text-nowrap">{periodLabel}</span>
        <Button
          title="Next"
          svgIcon={chevronRightIcon}
          disabled={offset >= 1}
          onClick={() => onOffsetChange(offset + 1)}
        />
      </div>
    </div>
    <div className="row">
      {cards.map(renderCard)}
      {goal && goal.Earned !== null && goal.QuarterlyCommissionBudget && (
        <div className="col-sm-6 col-md-4 col-lg-3 col-xl-2 mt-3">
          <div className="card h-100">
            <div className="card-body pb-0">
              <h5 className="text-muted fw-normal mt-0">Goal % Complete</h5>
              <ArcGauge
                scale={{ startAngle: -45, endAngle: 225, max: goal.QuarterlyCommissionBudget, rangeSize: 20, rangeLineCap: "butt" }}
                colors={[
                  { color: "#28a745", from: 0, to: goal.Earned || 0 },
                  { color: "#E3E3E3", from: goal.Earned || 0 }
                ]}
                value={goal.Earned || 0}
                arcCenterRender={() => <h3>{formatNumber(goal.Earned || 0, 'c')}</h3>}
              />
            </div>
          </div>
        </div>
      )}
    </div>
    <br />
    <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
      <h5 className="mb-0">{periodLabel} Revenue</h5>
      {chartSeriesOptions.length > 1 && chartAssignmentFilters}
    </div>
    <Chart transitions={false}>
      <ChartTooltip />
      <ChartLegend position="bottom" orientation="horizontal" />
      <ChartCategoryAxis>
        <ChartCategoryAxisItem
          categories={chartCategories}
          title={{ text: chartCategoryTitle }}
          axisCrossingValue={[0, chartCategories.length]}
        />
      </ChartCategoryAxis>
      <ChartSeries>
        <ChartSeriesItem name="Revenue" type="area" axis="revenue" color="#28a745" data={selectedDailySeries?.RevenueByDay ?? []} tooltip={{ format: "{0:C0}" }} />
        <ChartSeriesItem name="Order Count" type="line" axis="count" color="#dc3545" data={selectedDailySeries?.OrderCountByDay ?? []} tooltip={{ format: "{0}" }} />
      </ChartSeries>
      <ChartValueAxis>
        <ChartValueAxisItem title={{ text: "Revenue" }} name="revenue" color="#28a745" labels={{ format: "c0" }} />
        <ChartValueAxisItem title={{ text: "Order Count" }} name="count" color="#dc3545" labels={{ format: "n0" }} />
      </ChartValueAxis>
    </Chart>
    <br />
  </React.Fragment>;
};

export default DashboardTab;
